import React, { useEffect } from "react";
import styled from "styled-components/macro";
import { match } from "ts-pattern";
import { ErrorIcon, WarningIcon } from "../Icons/Icons";
import {
  DesktopBar,
  DesktopErrorIconArea,
  DesktopErrorMessage,
  DesktopWarningIconArea,
  DesktopWarningMessage,
} from "../Notifications/Notifications";
import { Citations } from "./Citations";
import { MessageContent } from "./MessageContent";
import { AnswerActions } from "./AnswerActions";
import { AssistantMessageFrame } from "./AssistantMessageFrame";
import { PendingAssistantBubble } from "./PendingAssistantBubble";
import { messageTimestamp } from "./messageTimestamp";
import type { ChatMessage, Feedback } from "./types";

const TimelineContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Message = styled.div`
  padding: ${({ theme }) => theme.resolve("spacing_lg")}
    ${({ theme }) => theme.resolve("spacing_xl")};
  border-radius: ${({ theme }) => theme.resolve("radius_lg")};
  background-color: #f6f7f9;
  border: ${({ theme }) => theme.resolve("border_md")} solid
    ${({ theme }) => theme.primaryBorder};
  max-width: 92%;
  font-size: ${({ theme }) => theme.resolve("font_size_md")};
  line-height: 21px;
`;

const UserMessage = styled(Message)`
  align-self: flex-end;
  background-color: ${({ theme }) =>
    theme.resolve("color_bg_surface_brand_mixed")};
  border-color: ${({ theme }) => theme.brandColor};
  white-space: pre-wrap;
  word-break: break-word;
`;

const AIMessage = styled(AssistantMessageFrame)`
  border-radius: ${({ theme }) => theme.resolve("radius_lg")};
  border-color: ${({ theme }) => theme.primaryBorder};
  background-color: white;
  && p:first-child {
    margin-block-start: 0;
    margin-top: 0;
  }
  && p:last-child {
    margin-block-end: 0;
    margin-bottom: 0;
  }
`;

const InformationMessage = styled(AssistantMessageFrame)`
  color: ${({ theme }) => theme.secondaryTextColor};
  font-size: ${({ theme }) => theme.resolve("font_size_sm")};
  padding-top: ${({ theme }) => theme.resolve("spacing_sm")};
  padding-bottom: ${({ theme }) => theme.resolve("spacing_sm")};
`;

// Inline-render the same notification visuals the global toast uses, so a
// chat error matches the look of a notifyError toast (without absolute
// positioning). Layout-only extension; colors/typography come from the
// shared notification components.
const InlineNotificationBar = styled(DesktopBar)`
  align-self: center;
  max-width: 92%;
`;

export const MessageTimeline = ({
  messages,
  tenantId,
  chatBotName,
  contentRef,
  onSetFeedback,
}: {
  messages: ChatMessage[];
  tenantId?: string;
  chatBotName?: string;
  contentRef: React.RefObject<HTMLDivElement>;
  onSetFeedback?: (clientMessageId: string, next: Feedback | null) => void;
}) => {
  const latestMessage = messages[messages.length - 1];
  useEffect(() => {
    if (contentRef?.current && messages.length > 0) {
      contentRef.current.scrollTo({
        top: contentRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
    // Ratings and comments must not pull readers away from older answers.
  }, [
    contentRef,
    messages.length,
    latestMessage?.id,
    latestMessage?.content,
    latestMessage?.pending,
    latestMessage?.progress,
    latestMessage?.stopping,
  ]);

  return (
    <TimelineContainer>
      {messages.map((message) =>
        match(message.role)
          .with("user", () => (
            <UserMessage
              key={message.id}
              title={messageTimestamp(message.created_at)}
            >
              {message.content}
            </UserMessage>
          ))
          .with("assistant", () =>
            message.pending ? (
              <PendingAssistantBubble
                key={message.id}
                createdAt={message.created_at}
                progress={message.progress}
                stopping={message.stopping}
              />
            ) : (
              <AIMessage
                key={message.id}
                title={messageTimestamp(message.created_at)}
              >
                <MessageContent
                  message={message}
                  tenantId={tenantId}
                  chatBotName={chatBotName}
                />
                {message.citations && message.citations.length > 0 && (
                  <Citations citations={message.citations} />
                )}
                <AnswerActions
                  message={message}
                  onSetFeedback={onSetFeedback}
                />
              </AIMessage>
            )
          )
          .with("error", () =>
            message.error_kind === "upstream" ? (
              <InlineNotificationBar
                key={message.id}
                role="alert"
                data-error-kind="upstream"
                title={messageTimestamp(message.created_at)}
              >
                <DesktopWarningIconArea>
                  <WarningIcon fill="#ffffff" />
                </DesktopWarningIconArea>
                <DesktopWarningMessage>{message.content}</DesktopWarningMessage>
              </InlineNotificationBar>
            ) : (
              <InlineNotificationBar
                key={message.id}
                role="alert"
                data-error-kind="fatal"
                title={messageTimestamp(message.created_at)}
              >
                <DesktopErrorIconArea>
                  <ErrorIcon fill="#ffffff" />
                </DesktopErrorIconArea>
                <DesktopErrorMessage>{message.content}</DesktopErrorMessage>
              </InlineNotificationBar>
            )
          )
          .with("info", () => (
            <InformationMessage
              key={message.id}
              role="status"
              title={messageTimestamp(message.created_at)}
            >
              {message.content}
            </InformationMessage>
          ))
          .exhaustive()
      )}
    </TimelineContainer>
  );
};
